import { useMemo } from "react";
import { CustomTable } from "../../../components/custom-table/CustomTable";
import type {
  SortDirection,
  TableColumn,
} from "../../../components/custom-table/types";
import type { DocumentType, Patient } from "../../../types";
import { ActionItemsCell } from "./cells/ActionItemsCell";
import { PatientNameCell } from "./cells/PatientNameCell";
import { PatientTagsCell } from "./cells/PatientTagsCell";
import {
  DiagnosisCell,
  DocumentNameCell,
  InsuranceCell,
  landingCancers,
  NextAppointmentCell,
  StageCell,
} from "./cells/patientCells";

interface PatientTableProps {
  patients: Patient[];
  documentType: DocumentType;
  onPatientSelect: (patient: Patient, documentType: DocumentType) => void;
  onPatientUpdate?: (patient: Patient) => void;
  /** Return an already-read row to unread (Action Items column). */
  onMarkUnread?: (patient: Patient, documentType: DocumentType) => void;
  /** Mark an unread row as read in place (Action Items column). */
  onMarkRead?: (patient: Patient, documentType: DocumentType) => void;
  /** Open the patient summary with the notes panel docked (Action Items column). */
  onOpenNotes?: (patient: Patient, documentType: DocumentType) => void;
  isLoading?: boolean;
  /** MRNs with at least one open note, used to light up the notes action. */
  mrnsWithNotes?: Set<string>;
  /** Controlled sort column key (persisted across navigation by the page). */
  sortKey?: string | null;
  /** Controlled sort direction. */
  sortDirection?: SortDirection;
  /** Sort-change callback; enables the table's controlled sort mode. */
  onSortChange?: (sortKey: string, direction: SortDirection) => void;
}

/**
 * Patient notifications table. Built on the shared custom-table pattern
 * (`CustomTable`): columns are declared as config with typed/custom cell
 * renderers. Sorting is pure single-column (no triage/combined ordering) — a
 * column click sorts the whole table by that column alone, with blank/"—" rows
 * always kept last (see custom-table `sorting.ts`).
 *
 * @param props - {@link PatientTableProps}
 * @returns The rendered notifications table
 */
export function PatientTable({
  patients,
  documentType,
  onPatientSelect,
  onPatientUpdate,
  onMarkUnread,
  onMarkRead,
  onOpenNotes,
  isLoading,
  mrnsWithNotes,
  sortKey,
  sortDirection,
  onSortChange,
}: PatientTableProps) {
  const columns = useMemo<TableColumn<Patient>[]>(
    () => [
      {
        key: "name",
        label: "Patient Name",
        width: "12%",
        sortable: true,
        sortAccessor: (p) => p.name,
        render: (p) => <PatientNameCell patient={p} />,
      },
      {
        key: "diagnosis",
        label: "Cancer Diagnosis Code",
        width: "24%",
        sortable: true,
        // Sort through `landingCancers` — the SAME resolver the cell renders from — so a row
        // showing "—" also sorts as empty and sinks (empties-last in sorting.ts). Reading
        // `p.dxCode` here instead looks equivalent but is not: it falls back to the MD-note
        // code, which can be a non-cancer ICD the cell refuses to show, so a dashed row would
        // sort by an invisible value.
        sortAccessor: (p) => landingCancers(p)[0]?.code ?? "",
        render: (p) => <DiagnosisCell patient={p} />,
      },
      {
        key: "stage",
        label: "Stage",
        width: "8%",
        align: "center",
        sortable: true,
        // Same rule as the Cancer Diagnosis Code column: sort the stage the cell actually
        // renders (the first landing cancer's), not the patient-level `p.stage`, which comes
        // from a different source and can be populated on a row that displays "-".
        sortAccessor: (p) => landingCancers(p)[0]?.stage ?? "",
        render: (p) => <StageCell patient={p} />,
      },
      {
        key: "nextAppointment",
        label: "Next Appointment",
        width: "11%",
        align: "center",
        sortable: true,
        sortAccessor: (p) => p.nextAppointment ?? "",
        render: (p) => <NextAppointmentCell patient={p} />,
      },
      {
        key: "insurance",
        label: "Insurance",
        width: "9%",
        align: "center",
        sortable: true,
        sortAccessor: (p) => p.insurance ?? "",
        render: (p) => <InsuranceCell patient={p} />,
      },
      {
        key: "documentName",
        label: "Document Name",
        width: "11%",
        render: (p) => (
          <DocumentNameCell patient={p} documentType={documentType} />
        ),
      },
      {
        key: "signoffDetails",
        label: "Signoff Details",
        width: "9%",
        align: "center",
        render: (p) =>
          p.signoffDetails ? (
            <div
              className="text-xs"
              style={{
                color: "var(--color-text-primary)",
                lineHeight: "1.4",
                textAlign: "center",
              }}
              title={p.signoffDetails}
            >
              {p.signoffDetails}
            </div>
          ) : (
            <span className="text-sm" style={{ color: "#9CA3AF" }}>
              —
            </span>
          ),
      },
      {
        key: "patientTags",
        label: "Patient Tags",
        width: "8%",
        align: "center",
        render: (p) => (
          <PatientTagsCell patient={p} onPatientUpdate={onPatientUpdate} />
        ),
      },
      {
        key: "actionItems",
        label: "Action Items",
        width: "8%",
        align: "center",
        render: (p) => (
          <ActionItemsCell
            patient={p}
            hasOpenNote={mrnsWithNotes?.has(p.mrn) ?? false}
            onOpenNotes={
              onOpenNotes ? () => onOpenNotes(p, documentType) : undefined
            }
            onMarkUnread={
              onMarkUnread ? () => onMarkUnread(p, documentType) : undefined
            }
            onMarkRead={
              onMarkRead ? () => onMarkRead(p, documentType) : undefined
            }
          />
        ),
      },
    ],
    [
      documentType,
      onPatientUpdate,
      onOpenNotes,
      onMarkUnread,
      onMarkRead,
      mrnsWithNotes,
    ],
  );

  return (
    <CustomTable<Patient>
      columns={columns}
      rows={patients}
      rowKey={(p) => p.id}
      onRowClick={(p) => onPatientSelect(p, documentType)}
      rowClassName={(p) => (p.isRead === false ? "ct-table__row--unread" : "")}
      initialSortKey="nextAppointment"
      initialSortDirection="asc"
      sortKey={sortKey}
      sortDirection={sortDirection}
      onSortChange={onSortChange}
      isLoading={isLoading}
      emptyMessage="No patients found matching the current filters."
    />
  );
}
